import _ from "lodash";
/**-- internal --*/
import {
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_UBD,
} from "Constants/stringConstants";

import {
  ENABLE_ENGAGEMENT_ANALYTICS,
  ENABLE_PERFORMANCE_ANALYTICS,
  ENABLE_BEHAVIOUR_ANALYTICS,
  ENABLE_BEHAVIOUR_MODULE_PERM,
  SNPv2_ACCESS_PERMISSION,
  SNPv2_MANAGE_EVALUATION_CYCLES_PERMISSION,
  TODDLE_ANALYTICS_USER_BASED_ACCESS_PERM,
  ATTENDANCE_DASHBOARD_ACCESS_PERMISSION,
  HOMEROOM_ACCESS_PERMISSION,
  CAN_VIEW_STUDENT_GOALS,
  SCHOOL_LIBRARY_VIEW_PERM,
  PROGRESS_REPORTS_PROGRAMME_LEVEL_VIEW_PERMISSION,
  VIEW_SCHOOL_POLICIES_AND_RESOURCES_MODULE_PERMISSION,
  ENABLE_TODDLE_ANALYTICS,
  ENABLE_MODULE_USAGE_ANALYTICS,
  CAN_VIEW_CALENDAR_MODULE_SETTING,
  ENABLE_ATTENDANCE_ANALYTICS,
  ENABLE_RNP_CONSUMPTION_IN_BEHAVIOUR,
  PLANNING_INSIGHTS_PROGRAMME_LEVEL_VIEW_PERMISSION,
  ADMIN_GRADEBOOK_ACCESS_PERMISSION,
  TIMETABLE_MANAGER_ACCESS_PERMISSION,
} from "Constants/permissionConstants";

import {
  mapleBearLikeCentralOrg,
  rolesAndPermissionsShowMessagingCard,
  isMapleBearLikeEducator,
  isTimetableWidgetEnabled,
} from "Courses/modules/utils";

import {
  MODULES,
  getAccessLevels,
  RULE_OPTIONS,
  hasModuleAdminAccess,
} from "lib/staffPermissionUtils";
import {
  isSentralPortalFeatureEnabled,
  getIsNewAdminUIEnabled,
} from "Utils/ACLStoreUtils";

import { isMessagingV2Enabled } from "MessagingV2/modules/Utils";
import ACLStore from "lib/aclStore";
import ACLStorage from "lib/aclService";

import {
  HOME_PAGE_FEATURE_ANNOUNCEMENT,
  HOME_PAGE_FEATURE_ATTENDANCE,
  HOME_PAGE_FEATURE_CLASSROOM_TODO,
  HOME_PAGE_FEATURE_CALENDAR,
  HOME_PAGE_FEATURE_FAMILY_COMMUNICATION,
  HOME_PAGE_FEATURE_MESSAGING,
  HOME_PAGE_FEATURE_PROGRESS_REPORT,
  HOME_PAGE_FEATURE_SNP,
  HOME_PAGE_FEATURE_LEARNER_PORTFOLIO,
  HOME_PAGE_FEATURE_EDUCATOR_CENTER,
  HOME_PAGE_FEATURE_GOALS,
  HOME_PAGE_FEATURE_MB_LIBRARY,
  HOME_PAGE_FEATURE_HOMEROOM,
  HOME_PAGE_FEATURE_DP_SUBJECT_CENTER,
  HOME_PAGE_FEATURE_TODDLE_ANALYTICS,
  HOME_PAGE_FEATURE_BEHAVIOR_MANAGEMENT,
  HOME_PAGE_FEATURE_MB_EDUCATOR_CENTER,
  HOME_PAGE_FEATURE_MB_CONTENT_SKILLS,
  HOME_PAGE_FEATURE_MB_PUBLISH_UNIT,
  HOME_PAGE_FEATURE_SCHOOL_LIBRARY,
  HOME_PAGE_FEATURE_TIMETABLE,
  HOME_PAGE_FEATURE_TIMETABLE_MANAGER,
  ESF_SENTRAL_ATTENDANCE,
  ESF_SENTRAL_WELLBEING,
  ESF_SENTRAL_TIMETABLE,
  ESF_SENTRAL_POLICIES,
  HOME_PAGE_FEATURE_PLANNING_INSIGHTS,
  HOME_PAGE_FEATURE_GRADEBOOK,
  ESF_SENTRAL_PXP,
  ESF_SENTRAL_NEWSFEED,
  ESF_SENTRAL_DAILY_NOTICE,
  HOME_PAGE_PORTFOLIO_REVIEW,
} from "Courses/components/TeacherCourses/Constants";
import { getHomePageFeatures } from "Courses/components/TeacherCourses/Utils";

import {
  typesConfig,
  tasksBasedModules,
  getConfigValue,
  isSentralConfigEnabled,
} from "AppComponents/GlobalSearchBar/components/Utils";

import {
  getIsSchoolAdminByRole,
  isOverallScoreEnabled,
  isRnpConsumptionEnabled,
} from "ToddleAnalytics/modules/Utils";
import { getIsRnpConsumptionAllowed } from "AttendanceDashboard/modules/Utils";
import { SENTRAL_PERMISSION_CONFIGURATIONS } from "Login/components/SentralPortalModal/SentralPortalUtils";
import { getSettingValue } from "modules/PermissionModule";
import { generateRnPContextsByLevel } from "Utils/RnPHelpers";
import { getCanAccessBehaviourDashboardInRnPOrg } from "Utils/BehaviourUtils";
import { isCurriculumInsightsEnabled } from "CurriculumInsights/modules/Utils";

const VISIBLE = "VISIBLE";
const NOT_VISIBLE = "NOT_VISIBLE";

export const getModuleSubLabel = ({ t, feature }) => {
  switch (feature) {
    case ESF_SENTRAL_WELLBEING:
    case ESF_SENTRAL_ATTENDANCE:
    case ESF_SENTRAL_TIMETABLE:
    case ESF_SENTRAL_POLICIES:
    case ESF_SENTRAL_PXP:
    case ESF_SENTRAL_NEWSFEED:
    case ESF_SENTRAL_DAILY_NOTICE: {
      return t("common:powered_by_sentral");
    }

    default: {
      return null;
    }
  }
};

const isToddleAnalyticsEnabled = ({
  currentCurriculumProgramType,
  currentCurriculumProgramId,
  getConfigValueProps,
}) => {
  const contextParameters = [
    {
      entityType: "CURRICULUM_PROGRAM",
      entityId: currentCurriculumProgramId,
    },
  ];

  if (isRnpConsumptionEnabled()) {
    return ACLStorage.getModuleSettingsValueByContext(
      ENABLE_TODDLE_ANALYTICS,
      contextParameters
    );
  }

  // Toddle analytics should be accessible to all school admins and non-admin users who have permission to access it
  const hasAccessToModule = !!getConfigValue({
    ...getConfigValueProps,
    configuration: TODDLE_ANALYTICS_USER_BASED_ACCESS_PERM,
  });
  if (!getIsSchoolAdminByRole() && !hasAccessToModule) return false;

  let isPerformanceAnalyticsEnabled = ACLStorage.getModuleSettingsValueByContext(
    ENABLE_PERFORMANCE_ANALYTICS,
    contextParameters
  );

  const isEngagementAnalyticsEnabled = ACLStorage.getModuleSettingsValueByContext(
    ENABLE_ENGAGEMENT_ANALYTICS,
    contextParameters
  );

  const isBehaviourAnalyticsEnabled = ACLStorage.getModuleSettingsValueByContext(
    ENABLE_BEHAVIOUR_ANALYTICS,
    contextParameters
  );

  const isModuleUsageAnalyticsEnabled = ACLStorage.getModuleSettingsValueByContext(
    ENABLE_MODULE_USAGE_ANALYTICS,
    contextParameters
  );

  const isAttendanceAnalyticsEnabled = ACLStorage.getModuleSettingsValueByContext(
    ENABLE_ATTENDANCE_ANALYTICS,
    contextParameters
  );

  // PYP doesn't have performance analytics
  if (_.isEqual(currentCurriculumProgramType, CURRICULUM_TYPE_PYP)) {
    isPerformanceAnalyticsEnabled = false;
  }

  // UBD performance analytics is accessible only if overall score is enabled
  if (_.isEqual(currentCurriculumProgramType, CURRICULUM_TYPE_UBD)) {
    isPerformanceAnalyticsEnabled =
      isPerformanceAnalyticsEnabled &&
      isOverallScoreEnabled({
        curriculumProgramId: currentCurriculumProgramId,
      });
  }

  return (
    isEngagementAnalyticsEnabled ||
    isPerformanceAnalyticsEnabled ||
    isBehaviourAnalyticsEnabled ||
    isModuleUsageAnalyticsEnabled ||
    isAttendanceAnalyticsEnabled
  );
};

const getShowProgressReportButtonCard = ({
  currentCurriculumProgramType,
  getConfigValueProps,
}) => {
  const { curriculumProgramId } = getConfigValueProps;

  const contexts = generateRnPContextsByLevel({
    level: "CURRICULUM_PROGRAM",
    curriculumProgramId,
  });

  const isModuleEnabled = ACLStorage.getModuleSettingsValueByContext(
    PROGRESS_REPORTS_PROGRAMME_LEVEL_VIEW_PERMISSION,
    _.first(contexts)
  );

  switch (currentCurriculumProgramType) {
    case CURRICULUM_TYPE_DP:
    case CURRICULUM_TYPE_MYP:
    case CURRICULUM_TYPE_UBD: {
      return isModuleEnabled;
    }
    // we dont show progress reports card in case of PYP curriculum
    case CURRICULUM_TYPE_PYP:
    default:
      return false;
  }
};

const HOME_PAGE_FEATURES_FOR_NON_CENTRAL_REPO_ORGANIZATIONS = [
  {
    key: HOME_PAGE_FEATURE_ANNOUNCEMENT,
    dataTestId: "button-dashboard-feature-announcement",
    getVisibilityStatus: ({
      isFutureAcademicYearSelected,
      getConfigValueProps,
    }) => {
      return !isFutureAcademicYearSelected &&
        !!getConfigValue({
          ...getConfigValueProps,
          configuration: "Common:Announcement",
        })
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_FEATURE_ATTENDANCE,
    dataTestId: "button-dashboard-feature-attendance-manager",
    getVisibilityStatus: ({
      isFutureAcademicYearSelected,
      isAdmin,
      getConfigValueProps,
    }) => {
      const { curriculumProgramId } = getConfigValueProps;

      const showTeacherAttendanceCard =
        !!getConfigValue({
          ...getConfigValueProps,
          configuration: "TeacherPortal:AttendanceDashboard",
        }) ||
        hasModuleAdminAccess({
          moduleName: MODULES.ATTENDANCE.id,
        });

      const isModuleEnabled = getIsRnpConsumptionAllowed()
        ? ACLStorage.getModuleSettingsValueByContext(
            ATTENDANCE_DASHBOARD_ACCESS_PERMISSION,
            [
              {
                entityId: curriculumProgramId,
                entityType: "CURRICULUM_PROGRAM",
              },
            ]
          )
        : (showTeacherAttendanceCard && !isAdmin) ||
          (!!getConfigValue({
            ...getConfigValueProps,
            configuration: "Common:AttendanceDashboard",
          }) &&
            isAdmin);

      const isSentralAttendanceEnabled = isSentralConfigEnabled(
        getConfigValueProps,
        SENTRAL_PERMISSION_CONFIGURATIONS.ATTENDANCE
      );

      return !isFutureAcademicYearSelected &&
        isModuleEnabled &&
        !isSentralAttendanceEnabled
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_FEATURE_CLASSROOM_TODO,
    dataTestId: "button-dashboard-classroom-review",
    getVisibilityStatus: ({
      isFutureAcademicYearSelected,
      getConfigValueProps,
    }) => {
      const showClassroomTodo = !!getConfigValue({
        ...getConfigValueProps,
        configuration: "Common:AssignmentsManager",
      });

      return !isFutureAcademicYearSelected && showClassroomTodo
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_FEATURE_CALENDAR,
    dataTestId: "button-dashboard-feature-my-calendar",
    getVisibilityStatus: ({ getConfigValueProps }) => {
      const { curriculumProgramId } = getConfigValueProps;
      const showCalendar = ACLStorage.getModuleSettingsValueByContext(
        CAN_VIEW_CALENDAR_MODULE_SETTING,
        [{ entityId: curriculumProgramId, entityType: "CURRICULUM_PROGRAM" }]
      );
      return showCalendar ? "VISIBLE" : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_FEATURE_FAMILY_COMMUNICATION,
    dataTestId: "button-dashboard-feature-family-messaging",
    getVisibilityStatus: ({
      isFutureAcademicYearSelected,
      isCurrentAcademicYearSelected,
      getConfigValueProps,
    }) => {
      return !isFutureAcademicYearSelected &&
        !!getConfigValue({
          ...getConfigValueProps,
          configuration: "TeacherPortal:FamilyCommunications",
        }) &&
        isCurrentAcademicYearSelected &&
        rolesAndPermissionsShowMessagingCard()
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_FEATURE_MESSAGING,
    dataTestId: "button-dashboard-feature-messagingV2",
    getVisibilityStatus: ({ isCurrentAcademicYearSelected }) => {
      return isMessagingV2Enabled() && isCurrentAcademicYearSelected
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_FEATURE_PROGRESS_REPORT,
    dataTestId: "button-dashboard-feature-progress-report",
    getVisibilityStatus: ({
      isFutureAcademicYearSelected,
      currentCurriculumProgramType,
      getConfigValueProps,
    }) => {
      return !isFutureAcademicYearSelected &&
        getShowProgressReportButtonCard({
          getConfigValueProps,
          currentCurriculumProgramType,
        })
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_FEATURE_SNP,
    dataTestId: "button-dashboard-feature-authorization-evaluation",
    getVisibilityStatus: ({ totalNewSnpCycles = 0, getConfigValueProps }) => {
      const { curriculumProgramId } = getConfigValueProps;

      const isModuleEnabled = ACLStorage.getModuleSettingsValueByContext(
        SNPv2_ACCESS_PERMISSION,
        [{ entityId: curriculumProgramId, entityType: "CURRICULUM_PROGRAM" }]
      );

      const canManageEvaluationCycles = ACLStorage.getPermissionValueByContext(
        SNPv2_MANAGE_EVALUATION_CYCLES_PERMISSION,
        [{ entityId: curriculumProgramId, entityType: "CURRICULUM_PROGRAM" }]
      );

      return isModuleEnabled &&
        (totalNewSnpCycles > 0 || canManageEvaluationCycles)
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_FEATURE_LEARNER_PORTFOLIO,
    dataTestId: "button-dashboard-feature-learner-portfolio",
    getVisibilityStatus: ({
      isFutureAcademicYearSelected,
      currentCurriculumProgramType,
      getConfigValueProps,
    }) => {
      return !isFutureAcademicYearSelected &&
        currentCurriculumProgramType == CURRICULUM_TYPE_DP &&
        !!getConfigValue({
          ...getConfigValueProps,
          configuration: "Common:LearnerPortfolio",
        })
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_FEATURE_EDUCATOR_CENTER,
    getVisibilityStatus: ({ getConfigValueProps }) => {
      const { curriculumProgramId } = getConfigValueProps;

      let canShowSchoolPoliciesAndResourcesModule;
      if (isRnpConsumptionEnabled()) {
        canShowSchoolPoliciesAndResourcesModule = ACLStorage.getModuleSettingsValueByContext(
          VIEW_SCHOOL_POLICIES_AND_RESOURCES_MODULE_PERMISSION,
          [{ entityId: curriculumProgramId, entityType: "CURRICULUM_PROGRAM" }]
        );
      } else {
        canShowSchoolPoliciesAndResourcesModule = getConfigValue({
          ...getConfigValueProps,
          configuration: "Common:EducatorCenterRevamp",
        });
      }

      const isSentralPoliciesEnabled = isSentralConfigEnabled(
        getConfigValueProps,
        SENTRAL_PERMISSION_CONFIGURATIONS.POLICIES_AND_RESOURCES
      );

      return canShowSchoolPoliciesAndResourcesModule &&
        !isSentralPoliciesEnabled
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_FEATURE_GOALS,
    getVisibilityStatus: ({
      isFutureAcademicYearSelected,
      getConfigValueProps,
    }) => {
      const { curriculumProgramId } = getConfigValueProps;

      const canShowGoalsCard = ACLStorage.getModuleSettingsValueByContext(
        CAN_VIEW_STUDENT_GOALS,
        [{ entityId: curriculumProgramId, entityType: "CURRICULUM_PROGRAM" }]
      );

      return !isFutureAcademicYearSelected && canShowGoalsCard
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_FEATURE_MB_LIBRARY,
    getVisibilityStatus: ({ organizationTags }) => {
      return isMapleBearLikeEducator({
        organizationTags,
      })
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_FEATURE_HOMEROOM,
    dataTestId: "button-dashboard-feature-attendance-homeroom",
    getVisibilityStatus: ({
      isFutureAcademicYearSelected,
      homeroomStudentCount,
      getConfigValueProps,
    }) => {
      const { curriculumProgramId } = getConfigValueProps;

      const isHomeroomPermission = getIsRnpConsumptionAllowed()
        ? ACLStorage.getModuleSettingsValueByContext(
            HOMEROOM_ACCESS_PERMISSION,
            [
              {
                entityId: curriculumProgramId,
                entityType: "CURRICULUM_PROGRAM",
              },
            ]
          )
        : ACLStore.can("TeacherPortal:Attendance") &&
          ACLStore.can("Common:Homeroom");

      const isHomeroomCardVisible =
        isHomeroomPermission && homeroomStudentCount > 0;

      return !isFutureAcademicYearSelected && isHomeroomCardVisible
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_FEATURE_DP_SUBJECT_CENTER,
    dataTestId: "button-dashboard-feature-subject-center",
    getVisibilityStatus: ({
      currentCurriculumProgramType,
      isFutureAcademicYearSelected,
      getConfigValueProps,
    }) => {
      const showDPSubjectCenterCard =
        _.isEqual(CURRICULUM_TYPE_DP, currentCurriculumProgramType) &&
        !!getConfigValue({
          ...getConfigValueProps,
          configuration: "FeatureFlag:SubjectCenter",
        });

      return !isFutureAcademicYearSelected && showDPSubjectCenterCard
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_FEATURE_TODDLE_ANALYTICS,
    dataTestId: "button-dashboard-feature-toddle-insights",
    getVisibilityStatus: ({
      isCurrentAcademicYearSelected,
      currentCurriculumProgramId,
      currentCurriculumProgramType,
      getConfigValueProps,
    }) => {
      return isCurrentAcademicYearSelected &&
        isToddleAnalyticsEnabled({
          currentCurriculumProgramType,
          currentCurriculumProgramId,
          getConfigValueProps,
        })
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_FEATURE_BEHAVIOR_MANAGEMENT,
    getVisibilityStatus: ({
      isFutureAcademicYearSelected,
      getConfigValueProps,
    }) => {
      const isRnPEnabledInBehaviour =
        isRnpConsumptionEnabled() &&
        ACLStore.can(ENABLE_RNP_CONSUMPTION_IN_BEHAVIOUR);

      if (isRnPEnabledInBehaviour) {
        const { curriculumProgramId } = getConfigValueProps;
        const hasModuleAccess = getCanAccessBehaviourDashboardInRnPOrg({
          curriculumProgramId,
        });

        return !isFutureAcademicYearSelected && hasModuleAccess
          ? "VISIBLE"
          : "NOT_VISIBLE";
      }

      const { hasStaffAccessPermission } = getAccessLevels({
        [MODULES.BEHAVIOUR_MANAGEMENT.id]: {
          hasStaffAccessPermission: {
            rule: RULE_OPTIONS.gte,
            value: MODULES.BEHAVIOUR_MANAGEMENT.values.TEACHER_ACCESS.id,
            defaultAccess: true,
          },
        },
      });

      const isModuleEnabled = !!getConfigValue({
        ...getConfigValueProps,
        configuration: ENABLE_BEHAVIOUR_MODULE_PERM,
      });

      const isSentralPortalWellbeingFeatureEnabled = isSentralConfigEnabled(
        getConfigValueProps,
        SENTRAL_PERMISSION_CONFIGURATIONS.WELLBEING
      );

      return !isFutureAcademicYearSelected &&
        isModuleEnabled &&
        hasStaffAccessPermission &&
        !isSentralPortalWellbeingFeatureEnabled
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_FEATURE_SCHOOL_LIBRARY,
    getVisibilityStatus: ({ getConfigValueProps }) => {
      const { curriculumProgramId } = getConfigValueProps;

      const isModuleEnabled = ACLStorage.getModuleSettingsValueByContext(
        SCHOOL_LIBRARY_VIEW_PERM,
        [{ entityId: curriculumProgramId, entityType: "CURRICULUM_PROGRAM" }]
      );

      return isModuleEnabled ? "VISIBLE" : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_FEATURE_TIMETABLE,
    dataTestId: "button-dashboard-feature-timetable",
    getVisibilityStatus: ({
      isFutureAcademicYearSelected,
      canViewTimetableWidget,
    }) => {
      const isSentralTimetableEnabled = isSentralPortalFeatureEnabled(
        SENTRAL_PERMISSION_CONFIGURATIONS.TIMETABLE
      );
      return !isFutureAcademicYearSelected &&
        isTimetableWidgetEnabled() &&
        !canViewTimetableWidget &&
        !isSentralTimetableEnabled
        ? VISIBLE
        : NOT_VISIBLE;
    },
  },
  {
    key: HOME_PAGE_FEATURE_TIMETABLE_MANAGER,
    dataTestId: "button-dashboard-feature-timetable-manager",
    getVisibilityStatus: ({
      isToddleV2TimetableEnabled,
      isFutureAcademicYearSelected,
      curriculumProgramIds,
    }) => {
      const curriculumProgramId = _.first(curriculumProgramIds);

      const isUserHasTimetableManagerAccess = ACLStorage.getModuleSettingsValueByContext(
        TIMETABLE_MANAGER_ACCESS_PERMISSION,
        [
          {
            entityType: "CURRICULUM_PROGRAM",
            entityId: curriculumProgramId,
          },
        ]
      );

      return !isFutureAcademicYearSelected &&
        isToddleV2TimetableEnabled &&
        isUserHasTimetableManagerAccess
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: ESF_SENTRAL_WELLBEING,
    getVisibilityStatus: ({ getConfigValueProps }) => {
      return isSentralConfigEnabled(
        getConfigValueProps,
        SENTRAL_PERMISSION_CONFIGURATIONS.WELLBEING
      )
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: ESF_SENTRAL_ATTENDANCE,
    getVisibilityStatus: ({ getConfigValueProps }) => {
      return isSentralConfigEnabled(
        getConfigValueProps,
        SENTRAL_PERMISSION_CONFIGURATIONS.ATTENDANCE
      )
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: ESF_SENTRAL_TIMETABLE,
    getVisibilityStatus: ({ getConfigValueProps }) => {
      return isSentralConfigEnabled(
        getConfigValueProps,
        SENTRAL_PERMISSION_CONFIGURATIONS.TIMETABLE
      )
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: ESF_SENTRAL_POLICIES,
    getVisibilityStatus: ({ getConfigValueProps }) => {
      return isSentralConfigEnabled(
        getConfigValueProps,
        SENTRAL_PERMISSION_CONFIGURATIONS.POLICIES_AND_RESOURCES
      )
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: ESF_SENTRAL_PXP,
    getVisibilityStatus: ({ getConfigValueProps }) => {
      return isSentralConfigEnabled(
        getConfigValueProps,
        SENTRAL_PERMISSION_CONFIGURATIONS.PXP
      )
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: ESF_SENTRAL_NEWSFEED,
    getVisibilityStatus: ({ getConfigValueProps }) => {
      return isSentralConfigEnabled(
        getConfigValueProps,
        SENTRAL_PERMISSION_CONFIGURATIONS.NEWSFEED
      )
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: ESF_SENTRAL_DAILY_NOTICE,
    getVisibilityStatus: ({ getConfigValueProps }) => {
      return isSentralConfigEnabled(
        getConfigValueProps,
        SENTRAL_PERMISSION_CONFIGURATIONS.NOTICE
      )
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },

  {
    key: HOME_PAGE_FEATURE_PLANNING_INSIGHTS,
    dataTestId: "button-dashboard-feature-planning-insights",
    getVisibilityStatus: ({ getConfigValueProps }) => {
      const { curriculumProgramId } = getConfigValueProps;

      // planning insights is superseded by curriculum insights
      if (isCurriculumInsightsEnabled()) {
        return "NOT_VISIBLE";
      }

      return ACLStorage.getModuleSettingsValueByContext(
        PLANNING_INSIGHTS_PROGRAMME_LEVEL_VIEW_PERMISSION,
        [{ entityId: curriculumProgramId, entityType: "CURRICULUM_PROGRAM" }]
      ) && getIsNewAdminUIEnabled()
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_FEATURE_GRADEBOOK,
    dataTestId: "button-dashboard-feature-gradebook",
    getVisibilityStatus: ({ getConfigValueProps }) => {
      const { curriculumProgramId } = getConfigValueProps;

      return ACLStorage.getModuleSettingsValueByContext(
        ADMIN_GRADEBOOK_ACCESS_PERMISSION,
        [{ entityId: curriculumProgramId, entityType: "CURRICULUM_PROGRAM" }]
      ) && getIsNewAdminUIEnabled()
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_PORTFOLIO_REVIEW,
    getVisibilityStatus: ({ organizationId, userId, getConfigValueProps }) => {
      const curriculumProgramId = _.get(
        getConfigValueProps,
        "curriculumProgramId"
      );
      const staffPermission = getSettingValue({
        name: "StaffPermission:PortfolioReviewer",
        organizationId,
        userId,
        currentCurriculumProgramId: curriculumProgramId,
      });

      return ACLStore.can("EnablePortfolioReviewer") &&
        _.includes(staffPermission, "REVIEW")
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
];

/* Future Academic Year condition will be revisited by 3A while AYT-V2 Sprint */
const HOME_PAGE_FEATURES_FOR_CENTRAL_REPO_ORGANIZATIONS = [
  {
    key: HOME_PAGE_FEATURE_MB_EDUCATOR_CENTER,
    getVisibilityStatus: ({ getConfigValueProps }) => {
      return getConfigValue({
        ...getConfigValueProps,
        configuration: "TeacherPortal:EducationCenter",
      })
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_FEATURE_MB_CONTENT_SKILLS,
    getVisibilityStatus: ({ getConfigValueProps }) => {
      return getConfigValue({
        ...getConfigValueProps,
        configuration: "TeacherPortal:ContentSkills",
      })
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_FEATURE_MESSAGING,
    dataTestId: "button-dashboard-feature-messagingV2",
    getVisibilityStatus: () => {
      return isMessagingV2Enabled() ? "VISIBLE" : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_FEATURE_MB_PUBLISH_UNIT,
    getVisibilityStatus: ({ getConfigValueProps }) => {
      return getConfigValue({
        ...getConfigValueProps,
        configuration: "TeacherPortal:ManageUnitPublish",
      })
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
  {
    key: HOME_PAGE_PORTFOLIO_REVIEW,
    getVisibilityStatus: ({
      organizationId,
      userId,
      currentCurriculumProgramId,
    }) => {
      const staffPermission = getSettingValue({
        name: "StaffPermission:PortfolioReviewer",
        organizationId,
        userId,
        currentCurriculumProgramId,
      });
      return ACLStore.can("EnablePortfolioReviewer") &&
        _.includes(staffPermission, "REVIEW")
        ? "VISIBLE"
        : "NOT_VISIBLE";
    },
  },
];

export const getHomePageFeaturesForGlobalSearchModal = ({
  isCurriculumProgramFree,
  organizationTags,
  isFutureAcademicYearSelected,
  isAdmin,
  currentCurriculumProgramType,
  userType,
  isCurrentAcademicYearSelected,
  organizationId,
  currentCurriculumProgramId,
  academicYearSelected,
  userId,
  userCurriculumPrograms,
  homeroomStudentCount,
  totalNewSnpCycles,
  identityId,
  role,
  isToddleV2TimetableEnabled,
  curriculumProgramIds,
}) => {
  /**
   * For free programs, no homepage features should be visible
   */
  if (isCurriculumProgramFree) return [];

  const getConfigValueProps = {
    curriculumProgramId: currentCurriculumProgramId,
    userId,
    organizationId,
    identityId,
    role,
    userType,
  };

  /**
   * An organisation can be a Maple Bear central repo or a regular organisation.
   * For regular case, below features should be considered.
   */
  if (!mapleBearLikeCentralOrg({ organizationTags })) {
    return _.filter(
      HOME_PAGE_FEATURES_FOR_NON_CENTRAL_REPO_ORGANIZATIONS,
      feature => {
        return (
          feature?.getVisibilityStatus?.({
            isFutureAcademicYearSelected,
            isAdmin,
            currentCurriculumProgramType,
            userType,
            isCurrentAcademicYearSelected,
            organizationId,
            currentCurriculumProgramId,
            academicYearSelected,
            userId,
            userCurriculumPrograms,
            organizationTags,
            homeroomStudentCount,
            totalNewSnpCycles,
            getConfigValueProps,
            isToddleV2TimetableEnabled,
            curriculumProgramIds,
          }) === "VISIBLE"
        );
      }
    );
  }

  /**
   * For central repo organisations, below features should be considered.
   */
  if (isFutureAcademicYearSelected) return [];

  return _.filter(
    HOME_PAGE_FEATURES_FOR_CENTRAL_REPO_ORGANIZATIONS,
    feature => {
      return (
        feature?.getVisibilityStatus?.({
          isFutureAcademicYearSelected,
          isAdmin,
          currentCurriculumProgramType,
          userType,
          isCurrentAcademicYearSelected,
          organizationId,
          currentCurriculumProgramId,
          academicYearSelected,
          userId,
          userCurriculumPrograms,
          organizationTags,
          homeroomStudentCount,
          totalNewSnpCycles,
          getConfigValueProps,
          isToddleV2TimetableEnabled,
          curriculumProgramIds,
        }) === "VISIBLE"
      );
    }
  );
};

export const getHomePageFeaturesV2 = props => {
  const isGlobalSearchRevampEnabled = ACLStore.can(
    "FeatureFlag:EnableGlobalSearchJumpToRevamp"
  );

  if (isGlobalSearchRevampEnabled) {
    return getHomePageFeaturesForGlobalSearchModal(props);
  } else {
    return getHomePageFeatures(props);
  }
};

export {
  getIsClassSearchV2Enabled,
  getIsClassSearchV3Enabled,
  getIsProjectGroupSearchEnabled,
  getIsInsightsSearchV2Enabled,
  CURRICULUM_PROGRAMME_ICON_STYLES,
  CURRICULUM_PROGRAMME_DUOTONE_ICON_COLOR_MAPPING,
  typesConfig,
  typesModulesList,
  modulesAsFiltersList,
  modulesAsFiltersListV2,
  modulesAsFiltersListV3,
  insightsBasedModules,
  getCourseBasedModulesList,
  adminBasedModules,
  homeBasedModules,
  homeBasedModulesShortcutMapping,
  adminBasedModulesShortcutMapping,
  tasksBasedModules,
  projectGroupBasedModules,
  MODULE_FOR_SORT_OVERRIDE,
  getOrgCurriculums,
  checkForExcludedCurriculum,
  checkIsModuleEnabled,
  getModuleItems,
  getCourseModuleItem,
  getIsLearningCourseEnabledInGlobalSearchCurriculum,
  getModulesFiltersList,
  getModuleTypeBySearchTerm,
  getCoursesBySearchTerm,
  getProjectGroupTypesLocalData,
  ALL_CURRICULUM_INDICATOR,
  getIsAllCurriculumSelected,
  filterRecentSearches,
  getCourseBasedModule,
  getRecentSearchItems,
  getImage,
  getChipImage,
  decideRoute,
  mapTypeToEndPoint,
  getFilterDataMemoized,
  parseResult,
  getAllConfigPerms,
  getParseResultMemoized,
  getCoursesBySearchTermMemoized,
  getModuleTypeBySearchTermMemoized,
  getProjectGroupTypesLocalDataMemoized,
  getCourseBasedModuleMemoized,
  getAllConfigPermsMemoized,
  getParsedLocalSuggestionsWithTimeout,
  getIsStudentUIDSearchEnabled,
} from "AppComponents/GlobalSearchBar/components/Utils";

const getPlatformSearchEntities = module => {
  if (!module) {
    return [];
  }
  switch (module) {
    case typesConfig.LE.id:
      return tasksBasedModules;
    default:
      return [module];
  }
};

const getSearchFilterData = ({
  academicYearSelectedId,
  curriculumIds,
  searchText = "",
  module,
}) => {
  const platformSearchEntities = getPlatformSearchEntities(module);

  return {
    searchText,
    platformSearchEntities,
    maxCountPerType: 10,
    scope: "PLATFORM",
    academicYear: academicYearSelectedId,
    curriculumIds,
    metadata: {},
  };
};

export const getSearchFilterDataMemoized = _.memoize(
  params => getSearchFilterData(params),
  params => JSON.stringify(params)
);

export const moduleGroupsConfig = {
  CLASSES_GROUP: {
    id: "CLASSES_GROUP",
    title: "common:course_plural",
  },
  LEARNING_COURSES_GROUP: {
    id: "LEARNING_COURSES_GROUP",
    title: "course:courses",
  },
  ADMIN_MODULES_GROUP: {
    id: "ADMIN_MODULES_GROUP",
    title: "common:admin_modules",
  },
  TASKS_MODULES_GROUP: {
    id: "TASKS_MODULES_GROUP",
    title: "common:task_plural",
  },
  HOME_MODULES_GROUP: {
    id: "HOME_MODULES_GROUP",
    title: "common:modules",
  },
  PROJECT_GROUP_MODULES_GROUP: {
    id: "PROJECT_GROUP_MODULES_GROUP",
    title: "common:projects",
  },
  INSIGHTS_MODULE_GROUP: {
    id: "INSIGHTS_MODULE_GROUP",
    title: "common:insights",
  },
};

// Array to maintain the priority order in search results
export const searchGroupPriorityOrder = [
  moduleGroupsConfig.ADMIN_MODULES_GROUP.id,
  moduleGroupsConfig.HOME_MODULES_GROUP.id,
];

export const getIsGlobalSearchJumpToV2Enabled = () => {
  return ACLStore.can("FeatureFlag:EnableGlobalSearchJumpToV2");
};

export const getVariablesForGlobalSearchPermissions = ({
  curriculumPrograms,
}) => {
  const contexts = [[]];

  const programsCount = _.size(curriculumPrograms);

  for (let i = 0; i < programsCount; i++) {
    const { id } = curriculumPrograms[i];

    const context = [
      {
        entityId: id,
        entityType: "CURRICULUM_PROGRAM",
      },
    ];

    contexts.push(context);
  }

  return {
    filters: {
      module: "GLOBAL_SEARCH",
      appType: ["TEACHER_WEB", "ADMIN_WEB"],
      contexts,
    },
  };
};

export const getCoursesV2QueryFilters = ({
  userId,
  userCurriculumProgramsIds,
}) => {
  return {
    userId,
    courseFilters: {
      curriculumProgramIds: userCurriculumProgramsIds,
      fetchOnlyMappedCourses: false,
      searchText: "",
      isOrderByStared: true,
    },
  };
};

export const getCoursesV2QueryFiltersMemoized = _.memoize(
  params => getCoursesV2QueryFilters(params),
  params => JSON.stringify(params)
);
